import { Vue, Component, Ref } from 'vue-property-decorator';
import * as DB from 'interfaces/database';
import CountryFlagGlob from 'components/Tabulator/CountryFlag';
import mitt from 'mitt';
import { TabEvent } from 'interfaces/app';
import { httpClient } from 'utils/http';
import { CellComponentExtended, ColumnDefinitionExtended, TabulatorFull as Tabulator } from 'tabulator-tables';
import { createInstance } from 'utils/vue';
import TabulatorBtn from 'components/Tabulator/TabulatorBtn';

const eventBus = mitt<TabEvent<DB.CountryModel>>();
@Component({
	components: {
		TabulatorBtn,
		CountryFlagGlob,
	},
})
export default class CountryTable extends Vue {
	@Ref('countryTable')
	private readonly tableReference!: HTMLDivElement;

	private isLoaded = false;

	private table?: Tabulator;

	private columnDefs: ColumnDefinitionExtended[] = []

	private get loggedIn(): boolean {
		return this.$auth.isAuthenticated;
	}

	protected beforeMount(): void {
		this.columnDefs = [
			{
				field: 'iso',
				title: 'ISO',
			},
			{
				title: 'Name',
				field: 'name',
				formatter: (cell: CellComponentExtended<DB.CountryModel>) => {
					const data = cell.getData();
					const instance = createInstance({
						component: CountryFlagGlob,
						props: {
							data,
						},
					});
					instance.$mount();

					return (instance.$el as HTMLElement);
				},
				headerFilter: true,
			},
			{
				title: 'Region',
				field: 'regionid',
			},
			{
				title: 'Default Currency',
				field: 'currency',
			},
			{
				title: 'Actions',
				formatter: (cell: CellComponentExtended<DB.BadgeModel>) => {
					const instance = createInstance({
						component: TabulatorBtn,
						props: {
							data: cell.getData(),
							buttons: [
								{
									id: 'edit',
									eventName: 'editItem',
									className: 'fa-edit',
								},
							],
							eventBus,
						},
					});
					instance.$mount();

					return (instance.$el as HTMLElement);
				},
			},
		];
	}

	protected mounted(): void {
		eventBus.on(
			'editItem',
			this.editCountry,
		);
		this.tableInitialization();
		this.table?.on(
			'tableBuilt',
			this.onTableBuilt,
		);
	}

	protected beforeDestroy(): void {
		eventBus.off(
			'editItem',
			this.editCountry,
		);
		this.table?.off(
			'tableBuilt',
			this.onTableBuilt,
		);
		this.table?.destroy();
	}

	private onTableBuilt(): void {
		this.getData();
	}

	private tableInitialization(): void {
		this.table = new Tabulator(
			this.tableReference,
			{
				height: '60vh',
				layout: 'fitColumns',
				columns: this.columnDefs,
			},
		);
	}

	private async getData(): Promise<void> {
		const parameter = new URLSearchParams({
			limit: '0',
		});
		this.table?.alert('Loading');
		try {
			const { data } = await httpClient.get<DB.CountryModel[]>(`/api/country?${parameter}`);
			this.table?.setData(data);
		} catch (err: any) {
			this.$bvToast.toast(
				`${err.message}`,
				{
					solid: true,
					variant: 'danger',
				},
			);
		} finally {
			this.table?.clearAlert();
		}
	}

	private editCountry(data: TabEvent<DB.CountryModel>['editItem']): void {
		this.$emit(
			'routeCountry',
			data.id,
		);
	}
}
